'use client';

import { useSite } from '@/contexts/SiteContext';
import { cn } from '@/utils/cn';
import { useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import type { Event_Entry } from '@/types/graphql';
import { formatDate } from '@/utils/dateFormat';
import {
    getShowHeaderImage,
    getShowPageLink,
    getShowProgramEnd,
    getShowProgramStart,
} from '@/utils/show';
import Image from 'next/image';
import Link from 'next/link';

// Define the Value type for react-calendar
type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

interface FloatingCalendarButtonProps {
    events: Event_Entry[];
}

export default function FloatingCalendarButton({ events }: FloatingCalendarButtonProps) {
    const { locale } = useSite();
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [open, setOpen] = useState(false);

    // Create a map of dates that have events
    const eventDates = new Set(
        events.map((event) => {
            const startDate = getShowProgramStart(event);
            return formatDate.compareFormat(startDate, locale);
        })
    );

    // Filter events for the selected date
    const selectedDateEvents = events.filter((event) => {
        const startDate = getShowProgramStart(event);
        return (
            formatDate.compareFormat(startDate, locale) ===
            formatDate.compareFormat(selectedDate, locale)
        );
    });

    const handleDateChange = (value: Value) => {
        if (value instanceof Date) {
            setSelectedDate(value);
        }
    };

    return (
        <div className="fixed right-4 bottom-4 z-50 font-neue-haas">
            {/* Calendar Button with Sheet */}
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild>
                    <button
                        type="button"
                        className={cn(
                            'rounded-full bg-haug-coral-100 p-4 text-haug-ivory-100 shadow-lg',
                            'transition-colors duration-200 hover:bg-haug-coral-70',
                            'flex items-center justify-center'
                        )}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-6 w-6"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <title>Calendar</title>
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                    </button>
                </SheetTrigger>
                <SheetContent className="h-full max-h-screen w-full overflow-y-auto bg-haug-ivory-100 p-4 pt-12 font-neue-haas sm:w-[90%] md:w-[450px]">
                    <div className="mt-2">
                        <Calendar
                            onChange={handleDateChange}
                            value={selectedDate}
                            locale={locale}
                            prev2Label={null}
                            next2Label={null}
                            tileClassName={({ date }: { date: Date }) => {
                                const dateStr = formatDate.compareFormat(date, locale);
                                return eventDates.has(dateStr) ? 'has-event' : '';
                            }}
                            className="mb-6 w-full border-none"
                        />

                        {/* Selected Date Events */}
                        {selectedDateEvents.length > 0 && (
                            <div className="mt-4 space-y-3">
                                <h3 className="mb-3 font-bold text-haug-lila-100">
                                    {formatDate.fullDayMonthYear(selectedDate, locale)}
                                </h3>
                                <div>
                                    {selectedDateEvents.map((event, idx) => {
                                        const image = getShowHeaderImage(event);
                                        const endTime = getShowProgramEnd(event);

                                        return (
                                            <Link
                                                key={event.id}
                                                href={getShowPageLink(event)}
                                                onClick={() => {
                                                    setOpen(false);
                                                }}
                                                aria-label="Buy Tickets"
                                                className={cn(
                                                    'flex items-center gap-3 rounded-lg transition-colors duration-200',
                                                    'bg-haug-ivory-30 hover:bg-haug-lila-70',
                                                    idx !== 0 && 'mt-3',
                                                    'w-full overflow-hidden'
                                                )}
                                            >
                                                {image?.url && (
                                                    <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded">
                                                        <Image
                                                            src={image.url}
                                                            alt={event.eventTitle || ''}
                                                            fill
                                                            sizes="64px"
                                                            className="object-cover"
                                                            style={{ mixBlendMode: 'luminosity' }}
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex min-w-0 flex-grow flex-col py-1">
                                                    <div
                                                        className="whitespace-wrap mb-0.5 font-bold text-base"
                                                        title={event.eventTitle || ''}
                                                    >
                                                        {event.eventTitle}
                                                    </div>
                                                    <div className="mb-0.5 text-haug-choco-100 text-xs">
                                                        {formatDate.time(
                                                            getShowProgramStart(event),
                                                            locale
                                                        )}
                                                        {endTime &&
                                                            ` - ${formatDate.time(endTime, locale)}`}
                                                    </div>
                                                </div>
                                                <div className="m-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-haug-coral-100 text-haug-ivory-100 shadow transition-colors duration-200 hover:bg-haug-coral-70">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                        className="h-4 w-4"
                                                    >
                                                        <title>Buy Tickets</title>
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M9 5l7 7-7 7"
                                                        />
                                                    </svg>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}
